import { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { initials } from "@/lib/format";

/**
 * MentionTextarea — a Textarea that shows an @-mention autocomplete popup
 * when the user types "@" followed by search chars. On selection, inserts
 * "@Full Name" into the text and pushes the user id into `onMentionAdd`.
 */
export default function MentionTextarea({
  value, onChange, users, onMentionAdd, placeholder, className, testId, onKeyDown: onExtraKeyDown,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [triggerPos, setTriggerPos] = useState(-1);
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);

  const filtered = (users || []).filter((u) =>
    !query || u.name.toLowerCase().includes(query.toLowerCase()) || u.email.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 6);

  useEffect(() => { setHighlight(0); }, [query]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const pos = e.target.selectionStart || 0;
    // find last @ before caret
    const before = v.slice(0, pos);
    const at = before.lastIndexOf("@");
    if (at >= 0) {
      const between = before.slice(at + 1);
      // trigger if @ is at start or after whitespace, and no whitespace since @
      const prev = at === 0 ? " " : before[at - 1];
      if (/\s/.test(prev) && !/\s/.test(between)) {
        setOpen(true);
        setQuery(between);
        setTriggerPos(at);
        return;
      }
    }
    setOpen(false);
    setTriggerPos(-1);
    setQuery("");
  };

  const pick = (u) => {
    if (triggerPos < 0 || !ref.current) return;
    const pos = ref.current.selectionStart || 0;
    const before = value.slice(0, triggerPos);
    const after = value.slice(pos);
    const inserted = `@${u.name} `;
    const next = before + inserted + after;
    onChange(next);
    onMentionAdd?.(u.id);
    setOpen(false);
    setQuery("");
    setTriggerPos(-1);
    // restore caret after inserted mention
    requestAnimationFrame(() => {
      if (ref.current) {
        const caret = (before + inserted).length;
        ref.current.focus();
        ref.current.setSelectionRange(caret, caret);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (open && filtered.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % filtered.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + filtered.length) % filtered.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(filtered[highlight]); return; }
      if (e.key === "Escape") { setOpen(false); return; }
    }
    // Delegate any un-handled keys (e.g. parent's Cmd+Enter to send)
    // only when the popup isn't swallowing the input.
    onExtraKeyDown?.(e);
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        data-testid={testId}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
      {open && filtered.length > 0 && (
        <div
          data-testid="mention-popup"
          className="absolute left-2 bottom-full mb-2 w-72 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-40"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase text-slate-500 tracking-wider border-b border-slate-100 bg-slate-50">
            Mention someone
          </div>
          {filtered.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onClick={() => pick(u)}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`mention-option-${u.email}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left ${i === highlight ? "bg-[#39A36E]/10" : "hover:bg-slate-50"}`}
            >
              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium">
                {initials(u.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
              </div>
              <span className="text-[10px] chip !text-slate-500 !border-slate-200 bg-slate-50 capitalize">{u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
